import {createSlice, current} from '@reduxjs/toolkit';
import {
    checkCauseForConcernForm,
    checkCFSForm,
    checkSafeguardingLevelForm,
    isMentalHealthFormComplete,
    isMentalHealthFormNeeded,
    PATIENT_CONTEXT_TYPE,
} from '../../util/patientContextUtil';
import {addWorkflowStep, clearWorkflow, nextWorkflowStep} from './workflow';
import _ from 'lodash';
import {setSnackbar} from './globals';
import {PATIENT_BANNER_TYPE, PATIENT_CONTEXT_TABS_ID} from '../../pages/ward/forms/formsUtil';
import {closeAllActionPanel, setActionPanelDisableComponent} from './globalModal';
import {GLOBAL_PANEL_ACTION_TYPE} from '../../components/actionPanel/components/panelType';
import {WORKFLOW_OPTIONS} from '../../util/workflowUtil';
import {formIds} from '../../pages/patient/patientContext/components/forms/formsId';
import {setDemographicPatient, setEditPatientActivity} from './patient';
import { sendUserAudit } from './user';
import { AUDIT_TYPES } from '../../util/auditUtil';

export const BASE_TAB = PATIENT_CONTEXT_TABS_ID.PATIENT;

const SPLIT_SCREEN_DEFAULT = {
    enabled: false,
    activeScreen: 1,
    showFormOnScreen: 1,
    screenOne: null,
    screenTwo: PATIENT_CONTEXT_TABS_ID.EMPTY,
    screenOneForms: [],
    screenTwoForms: [],
};

const PATIENT_OBS_SETTINGS_DEFAULT = {
    obsType: null,
    obsAgeRange: null,
    neuroAgeRange: null,
};

const SEARCH_INIT_STATE = {
    minAgeRange: 0,
    maxAgeRange: 130,
    patientsMode: 'all',
    searchOnlyNumberType: 'all',
    advancedOptions: null,
};

const ASSESSMENT_INIT_STATE = {
    ageRestriction: [],
    specialities: [],
    clinics: [],
};

export const INIT_TIMESTAMP_PATIENT = {
    patient: null,
    timestamp: null,
};

export const INIT_PREV_OBS_STATS = {
    totalScore: null,
    riskScore: null,
    obsDate: null,
};

const INIT_STATE = {
    currentTab: BASE_TAB,
    valueToModify: null,
    historyPatient: null,
    patientBed: null,
    ageUnknown: false,
    patientType: null,
    patientAgeType: null,
    counterVisitsED: null,
    counterVisitsIP: null,
    formIsOpen: false,
    isFullFormOpen: false,
    isFormMultiContainerOpen: false,
    isBlockExitForm: false,
    isPatientLocked: false,
    forceSelectLastForm: null,
    dialogDetails: null,
    clinicalConclusionNeeded: {
        open: false,
        fromDischarge: false,
    },
    fullFormsQueue: [],
    miniFormsQueue: [],
    tempCurrentTab: null,
    openWarningFormOpen: false,
    splitScreen: SPLIT_SCREEN_DEFAULT,
    eMedLoad: false,
    loadId: null,
    patientObsSettings: PATIENT_OBS_SETTINGS_DEFAULT,
    searchFilters: SEARCH_INIT_STATE,
    assessmentFilters: ASSESSMENT_INIT_STATE,
    patientBannerType: PATIENT_BANNER_TYPE.UNSET,
    foreOpenNewClinicalNote: false,
    openBarcodeModal: false,
    openAdvanceSearch: false,
    openAgeSearch: false,
    observationType: GLOBAL_PANEL_ACTION_TYPE.NEWS_OBSERVATION,
    observationViewType: 'table',
    observationMethod: null,
    removeOption: null,
    openFormTimestamp: INIT_TIMESTAMP_PATIENT,
    prevObsStats: INIT_PREV_OBS_STATS,
    splitScreenAlert: false,
    dischargeTemplate: null,
    patientPastActivity: false,
    referralModalClosed: false,
    referralFormData: null,
    blockDischargeMandatoryAssessment: false,
    showMandatoryAssessment: false,
    mandatoryAssessmentList: [],
    currentPatientInWard: null,
    showDischargeTemplates: false,
};

export const patientContextSlice = createSlice({
    name: 'patientContext',
    initialState: INIT_STATE,
    reducers: {
        resetContext: () => INIT_STATE,
        setPatientBed: (state, payload) => {
            state.patientBed = payload.payload;
        },
        setCurrentTab: (state, payload) => {
            state.currentTab = payload.payload;
        },
        setTempCurrentTab: (state, payload) => {
            state.tempCurrentTab = payload.payload;
        },
        setAgeUnknown: (state, payload) => {
            state.ageUnknown = payload.payload;
        },
        setCounterVisitsED: (state, payload) => {
            state.counterVisitsED = payload.payload;
        },
        setCounterVisitsIP: (state, payload) => {
            state.counterVisitsIP = payload.payload;
        },
        setFormIsOpen: (state, payload) => {
            state.formIsOpen = payload.payload;
        },
        setFullFormOpen: (state, payload) => {
            state.isFullFormOpen = payload.payload;
        },
        setForceSelectLastForm: (state, payload) => {
            state.forceSelectLastForm = payload.payload;
        },
        setIsPatientLocked: (state, payload) => {
            state.isPatientLocked = payload.payload;
        },
        setContextDialogTypeOpen: (state, payload) => {
            state.dialogTypeOpen = payload.payload;
        },
        setClinicalConclusionNeeded: (state, payload) => {
            state.clinicalConclusionNeeded = payload.payload;
        },
        setValueToModify: (state, payload) => {
            state.valueToModify = payload.payload;
        },
        setFullFormsQueue: (state, payload) => {
            state.fullFormsQueue = payload.payload;
        },
        setMiniFormsQueue: (state, payload) => {
            state.miniFormsQueue = payload.payload;
        },
        setOpenWarningFormOpen: (state, payload) => {
            state.openWarningFormOpen = payload.payload;
        },
        setEMedLoad: (state, payload) => {
            state.eMedLoad = payload.payload;
        },
        setBlockExitForm: (state, payload) => {
            state.isBlockExitForm = payload.payload;
        },
        setContextSplitScreen: (state, payload) => {
            state.splitScreen = {
                ...state.splitScreen,
                ...payload.payload,
            };
        },
        setPatientType: (state, payload) => {
            state.patientType = payload.payload;
        },
        setPatientAgeType: (state, payload) => {
            state.patientAgeType = payload.payload;
        },
        setLoadId: (state, payload) => {
            state.loadId = payload.payload;
        },
        setPatientObsSettings: (state, payload) => {
            state.patientObsSettings = payload.payload;
        },
        setPatientBannerType: (state, payload) => {
            state.patientBannerType = payload.payload;
        },
        setForeOpenNewClinicalNote: (state, payload) => {
            state.foreOpenNewClinicalNote = payload.payload;
        },
        setOpenBarcodeModal: (state, payload) => {
            state.openBarcodeModal = payload.payload;
        },
        setHistoryPatient: (state, payload) => {
            state.historyPatient = payload.payload;
        },
        setOpenAdvanceSearch: (state, payload) => {
            state.openAdvanceSearch = payload.payload;
        },
        setOpenAgeSearch: (state, payload) => {
            state.openAgeSearch = payload.payload;
        },
        setObservationType: (state, payload) => {
            state.observationType = payload.payload;
        },
        setObservationViewType: (state, payload) => {
            state.observationViewType = payload.payload;
        },
        setObservationMethod: (state, payload) => {
            state.observationMethod = payload.payload;
        },
        setAssessmentFilters: (state, payload) => {
            state.assessmentFilters = payload.payload;
        },
        setRemoveOption: (state, payload) => {
            state.removeOption = payload.payload;
        },
        setFormMultiContainerOpen: (state, payload) => {
            state.isFormMultiContainerOpen = payload.payload;
        },
        setPrevObsStats: (state, payload) => {
            state.prevObsStats = payload.payload;
        },
        setSplitScreenAlert: (state, payload) => {
            state.splitScreenAlert = payload.payload;
        },
        setDischargeTemplate: (state, payload) => {
            state.dischargeTemplate = payload.payload;
        },
        setPatientPastActivity: (state, payload) => {
            state.patientPastActivity = payload.payload;
        },
        setReferralModalClosed: (state, payload) => {
            state.referralModalClosed = payload.payload;
        },
        setReferralFormData: (state, payload) => {
            state.referralFormData = payload.payload;
        },
        setBlockDischargeMandatoryAssessment: (state, payload) => {
            state.blockDischargeMandatoryAssessment = payload.payload;
        },
        setShowMandatoryAssessment: (state, payload) => {
            state.showMandatoryAssessment = payload.payload;
        },
        setMandatoryAssessmentList: (state, payload) => {
            state.mandatoryAssessmentList = payload.payload;
        },
        setCtxCurrentPatientInWard: (state, payload) => {
            state.currentPatientInWard = payload.payload;
        },
        setOpenFormTimestamp: (state, payload) => {
            if (!payload.payload) {
                state.openFormTimestamp = INIT_TIMESTAMP_PATIENT;
            } else {
                state.openFormTimestamp = payload.payload;
            }
        },
        setSearchFilters: (state, payload) => {
            state.searchFilters = {
                ...state.searchFilters,
                ...payload.payload,
            };
        },
        setShowDischargeTemplates:(state, action) => {
            state.showDischargeTemplates = action.payload;
        },
    },
});

// Setters
export const {
    resetContext,
    setPatientBed,
    setAgeUnknown,
    setCounterVisitsED,
    setCounterVisitsIP,
    setCurrentTab,
    setTempCurrentTab,
    setFormIsOpen,
    setFullFormOpen,
    setIsPatientLocked,
    setContextDialogTypeOpen,
    setClinicalConclusionNeeded,
    setValueToModify,
    setFullFormsQueue,
    setMiniFormsQueue,
    setOpenWarningFormOpen,
    setContextSplitScreen,
    setEMedLoad,
    setBlockExitForm,
    setForceSelectLastForm,
    setPatientType,
    setPatientAgeType,
    setLoadId,
    setPatientObsSettings,
    setSearchFilters,
    setPatientBannerType,
    setForeOpenNewClinicalNote,
    setOpenBarcodeModal,
    setHistoryPatient,
    setOpenAdvanceSearch,
    setOpenAgeSearch,
    setObservationViewType,
    setObservationMethod,
    setObservationType,
    setAssessmentFilters,
    setRemoveOption,
    setOpenFormTimestamp,
    setFormMultiContainerOpen,
    setPrevObsStats,
    setSplitScreenAlert,
    setDischargeTemplate,
    setPatientPastActivity,
    setReferralModalClosed,
    setReferralFormData,
    setBlockDischargeMandatoryAssessment,
    setShowMandatoryAssessment,
    setMandatoryAssessmentList,
    setCtxCurrentPatientInWard,
    setShowDischargeTemplates,
} = patientContextSlice.actions;

// Variable
export const ctxCurrentTab = state => state.patientContext.currentTab;
export const ctxTempCurrentTab = state => state.patientContext.tempCurrentTab;
export const ctxPatientBed = state => state.patientContext.patientBed;
export const ctxAgeUnknown = state => state.patientContext.ageUnknown;
export const ctxPatientAgeType = state => state.patientContext.patientAgeType;
export const ctxPatientType = state => state.patientContext.patientType;
export const ctxCounterVisitsED = state => state.patientContext.counterVisitsED;
export const ctxCounterVisitsIP = state => state.patientContext.counterVisitsIP;
export const ctxFormIsOpen = state => state.patientContext.formIsOpen;
export const ctxFullFormOpen = state => state.patientContext.isFullFormOpen;
export const ctxPatientLocked = state => state.patientContext.isPatientLocked;
export const ctxDialogTypeOpen = state => state.patientContext.dialogTypeOpen;
export const ctxClinicalConclusionNeeded = state => state.patientContext.clinicalConclusionNeeded;
export const ctxValueToModify = state => state.patientContext.valueToModify;
export const ctxFullFormsQueue = state => state.patientContext.fullFormsQueue;
export const ctxMiniFormsQueue = state => state.patientContext.miniFormsQueue;
export const ctxOpenWarningFormOpen = state => state.patientContext.openWarningFormOpen;
export const ctxSplitScreen = state => state.patientContext.splitScreen;
export const ctxEMedLoad = state => state.patientContext.eMedLoad;
export const ctxBlockExitForm = state => state.patientContext.isBlockExitForm;
export const ctxForceSelectLastForm = state => state.patientContext.forceSelectLastForm;
export const ctxIdFormToLoad = state => state.patientContext.loadId;
export const ctxObsSettings = state => state.patientContext.patientObsSettings;
export const ctxSearchFilters = state => state.patientContext.searchFilters;
export const ctxPatientBannerType = state => state.patientContext.patientBannerType;
export const ctxForeOpenNewClinicalNote = state => state.patientContext.foreOpenNewClinicalNote;
export const ctxOpenBarcodeModal = state => state.patientContext.openBarcodeModal;
export const ctxHistoryPatient = state => state.patientContext.historyPatient;
export const ctxOpenAdvanceSearch = state => state.patientContext.openAdvanceSearch;
export const ctxOpenAgeSearch = state => state.patientContext.openAgeSearch;
export const ctxObservationType = state => state.patientContext.observationType;
export const ctxObservationViewType = state => state.patientContext.observationViewType;
export const ctxObservationMethod = state => state.patientContext.observationMethod;
export const ctxAssessmentFilters = state => state.patientContext.assessmentFilters;
export const ctxRemoveOption = state => state.patientContext.removeOption;
export const ctxOpenFormTimestamp = state => state.patientContext.openFormTimestamp;
export const ctxFormMultiContainerOpen = state => state.patientContext.isFormMultiContainerOpen;
export const ctxObsPrevStats = state => state.patientContext.prevObsStats;
export const ctxSplitScreenAlert = state => state.patientContext.splitScreenAlert;
export const ctxDischargeTemplate = state => state.patientContext.dischargeTemplate;
export const ctxPatientPastActivity = state => state.patientContext.patientPastActivity;
export const ctxReferralModalClosed = state => state.patientContext.referralModalClosed;
export const ctxReferralFormData = state => state.patientContext.referralFormData;
export const ctxBlockDischargeMandatoryAssessment = state => state.patientContext.blockDischargeMandatoryAssessment;
export const ctxShowMandatoryAssessment = state => state.patientContext.showMandatoryAssessment;
export const ctxMandatoryAssessmentList = state => state.patientContext.mandatoryAssessmentList;
export const ctxCurrentPatientInWard = state => state.patientContext.currentPatientInWard;
export const ctxShowDischargeTemplates = (state) => state.patientContext.showDischargeTemplates;

// Functions
export const goToBaseTab = () => (dispatch) => {
    dispatch(setCurrentTab(BASE_TAB));
    dispatch(setFullFormOpen(false));
    dispatch(setContextSplitScreen(SPLIT_SCREEN_DEFAULT));
};

export const closePatientContext = () => (dispatch) => {
    dispatch(resetContext());
};

export const changePatientContextTab = (tab, force = false) => (dispatch, getState) => {
    const isFullFormOpened = getState().patientContext.isFullFormOpen;
    const isBlockExitForm = getState().patientContext.isBlockExitForm;
    if (isBlockExitForm) {
        dispatch(setSnackbar({type: 'warning', message: 'Save the form to move to another tab.'}));
    } else if (isFullFormOpened && !force) {
        dispatch(setTempCurrentTab(tab));
        dispatch(setOpenWarningFormOpen(true));
    } else {
        dispatch(setCurrentTab(tab));
    }
};

export const checkPatientWorkflow = (currentPatientInWard, currentPatient, isIP, patientType) => (dispatch) => {
    if (patientType === PATIENT_CONTEXT_TYPE.REVIEW) return null;

    if ((!currentPatientInWard || currentPatient?._id !== currentPatientInWard?._id) && !isIP && patientType) {
        let workflowSteps = [];
        if (isMentalHealthFormNeeded(currentPatient.triage) && !isMentalHealthFormComplete(currentPatient))
            workflowSteps.push(WORKFLOW_OPTIONS.OPEN_MENTAL_HEALTH_DIALOG);

        if (checkCauseForConcernForm(currentPatient))
            workflowSteps.push(WORKFLOW_OPTIONS.OPEN_CAUSE_FOR_CONCERN_DIALOG);

        if (checkSafeguardingLevelForm(currentPatient))
            workflowSteps.push(WORKFLOW_OPTIONS.OPEN_SAFEGUARDING_DIALOG);

        if (checkCFSForm(currentPatient))
            workflowSteps.push(WORKFLOW_OPTIONS.OPEN_CFS_DIALOG);

        dispatch(addWorkflowStep(workflowSteps));
        dispatch(nextWorkflowStep());
    }
};

export const openPatientForm = (formId) => (dispatch, getState) => {
    const currentOpenedFullForms = _.cloneDeep(getState().patientContext.fullFormsQueue);
    const currentOpenedMiniForms = _.cloneDeep(getState().patientContext.miniFormsQueue);
    let splitFormId = formId;
    if (_.startsWith(splitFormId, '$multi_')) {
        splitFormId = _.replace(splitFormId, '$multi_', '');
    }

    if (splitFormId.includes('_$id_')) {
        const splitFormIdArray = splitFormId.split('_$id_');
        splitFormId = splitFormIdArray[0];
    }

    const form = _.find(formIds, f => f.id === splitFormId);
    if (form) {
        dispatch(auditForm(form, false));
        dispatch(pushOrRemoveToSplitScreenQueue(formId, false))
        const isFull = !form.isMiniForm;
        const queue = isFull ? currentOpenedFullForms : currentOpenedMiniForms;
        const index = _.findIndex(queue, q => q === splitFormId);
        if (index === -1) {
            queue.push(formId);
        }
        if (isFull) {
            dispatch(setFullFormsQueue(currentOpenedFullForms));
        } else {
            dispatch(setMiniFormsQueue(currentOpenedMiniForms));
        }
    } else {
        dispatch(setSnackbar({type: 'error', message: 'Assessment form configuration not found.'}));
    }
};

export const closePatientForm = (formId) => (dispatch, getState) => {
    const currentOpenedFullForms = _.cloneDeep(getState().patientContext.fullFormsQueue);
    const currentOpenedMiniForms = _.cloneDeep(getState().patientContext.miniFormsQueue);
    let splitFormId = formId;
    if (_.startsWith(splitFormId, '$multi_')) {
        splitFormId = _.replace(splitFormId, '$multi_', '');
    }

    if (splitFormId.includes('_$id_')) {
        const splitFormIdArray = splitFormId.split('_$id_');
        splitFormId = splitFormIdArray[0];
    }

    const form = _.find(formIds, f => f.id === splitFormId);
    if (form) {
        dispatch(auditForm(form, true));
        dispatch(pushOrRemoveToSplitScreenQueue(formId, true))
        const isFull = !form.isMiniForm;
        const queue = isFull ? currentOpenedFullForms : currentOpenedMiniForms;
        const index = _.findIndex(queue, q => q === formId);
        if (index > -1) {
            queue.splice(index, 1);
        }
        if (isFull) {
            dispatch(setFullFormsQueue(currentOpenedFullForms));
        } else {
            dispatch(setMiniFormsQueue(currentOpenedMiniForms));
        }
    }
};

const auditForm = (form, isClosed) => (dispatch, getState) => {
  const currentPatient = getState().patientContext.currentPatientInWard;
  let action = AUDIT_TYPES.OPEN_AGYLE_FORM;
  let data = {
    isOpenForm: !isClosed,
  };

  switch (form.id) {
    case "clerkingED":
      action = AUDIT_TYPES.OPEN_CLERKING_ED;
      break;
    case "triage":
      action = AUDIT_TYPES.OPEN_TRIAGE;
      break;
    case "snt":
      action = AUDIT_TYPES.OPEN_SNT;
      break;
    case "fitnessForWork":
      action = AUDIT_TYPES.OPEN_EMED3;
      break;
    default:
      action = AUDIT_TYPES.OPEN_AGYLE_FORM;
      data = {
        ...data,
        formId: form.id,
      };
      break;
  }
  dispatch(sendUserAudit(currentPatient, action, data));
};

const pushOrRemoveToSplitScreenQueue = (formId, remove) => (dispatch, getState) => {
    const splitScreen = getState().patientContext.splitScreen;
    if (splitScreen?.enabled) {
        const activeScreen = splitScreen.activeScreen;
        const formQueue = _.cloneDeep(activeScreen === 1 ? splitScreen.screenOneForms : splitScreen.screenTwoForms);
        const index = _.findIndex(formQueue, q => q === formId);
        if (index === -1 && !remove) {
            formQueue.push(formId);
        } else if (index > -1 && remove) {
            formQueue.splice(index, 1);
        }
        dispatch(setContextSplitScreen({
            [activeScreen === 1 ? 'screenOneForms' : 'screenTwoForms']: formQueue,
        }));
    }
};

export const modifyValuesInPatientInWard = (values, patient, updatePatient) => (dispatch) => {
    const clone = _.cloneDeep(patient);
    let referralsAdded = false;

    values.forEach((value) => {
        referralsAdded = referralsAdded || value.name === 'studies.referrals';
        _.set(clone, value.name, value.value);
    });

    if (updatePatient) updatePatient(clone);

    if (referralsAdded && checkSafeguardingLevelForm(clone)) {
        dispatch(addWorkflowStep([WORKFLOW_OPTIONS.OPEN_SAFEGUARDING_DIALOG]));
        dispatch(nextWorkflowStep());
    }
};

export const toggleSplitScreen = (forceOpenContext = null) => (dispatch, getState) => {
    const activeScreen = getState().patientContext.splitScreen.enabled;
    const currentTab = getState().patientContext.currentTab;
    const fullFormQueue = getState().patientContext.fullFormsQueue;
    const miniFormQueue = getState().patientContext.miniFormsQueue;

    dispatch(setContextSplitScreen({
        enabled: !activeScreen,
        screenOne: !activeScreen ? currentTab : null,
        screenTwo: forceOpenContext || PATIENT_CONTEXT_TABS_ID.EMPTY,
        activeScreen: 1,
        screenOneForms: !activeScreen ? [...fullFormQueue, ...miniFormQueue] : [],
        screenTwoForms: [],
    }));

    dispatch(setSnackbar({type: 'info', message: `Split screen is now ${!activeScreen ? 'enabled' : 'disabled'}.`}));
};

export const changeActiveContextScreen = (value) => (dispatch, getState) => {
    const activeScreen = getState().patientContext.splitScreen.activeScreen;
    const screenOne = getState().patientContext.splitScreen.screenOne;
    const screenTwo = getState().patientContext.splitScreen.screenTwo;
    const isScreenOneActive = activeScreen === 1;

    const differentScreens = isScreenOneActive ? (screenTwo !== value) : (screenOne !== value);
    if (differentScreens) {
        dispatch(setContextSplitScreen({
            [isScreenOneActive ? 'screenOne' : 'screenTwo']: value || null,
        }));
    } else {
        dispatch(setSnackbar({type: 'warning', message: 'You can\'t have two of the same active screens.'}));
    }
};

export const openSplitScreenWithRegistry = (patient, editDemographic) => (dispatch, getState) => {
    if (patient?.patient?.tags?.includes('temporary')) {
        dispatch(setSnackbar({type: 'warning', message: 'Patient is temporary and cannot be edited.'}));
    } else {
        const action = editDemographic ? AUDIT_TYPES.EDIT_PATIENT_DEMOGRAPHIC : AUDIT_TYPES.EDIT_PATIENT_ACTIVITY;
        dispatch(sendUserAudit(patient, action));
        
        const splitScreenEnabled = getState().patientContext.splitScreen.enabled;
        const screenOne = getState().patientContext.splitScreen.screenOne;

        if (editDemographic) dispatch(setDemographicPatient(patient));
        else dispatch(setEditPatientActivity(patient));

        if (splitScreenEnabled) {
            const updatedScreen = screenOne === PATIENT_CONTEXT_TABS_ID.PATIENT
                ? {screenTwo: PATIENT_CONTEXT_TABS_ID.REGISTRY}
                : {screenOne: PATIENT_CONTEXT_TABS_ID.REGISTRY};

            dispatch(setContextSplitScreen(updatedScreen));
        } else {
            dispatch(toggleSplitScreen(PATIENT_CONTEXT_TABS_ID.REGISTRY));
        }
    }
};

export const clearSearchFilter = () => (dispatch) => {
    dispatch(setSearchFilters(SEARCH_INIT_STATE));
};


export const clearAssessmentFilter = () => (dispatch) => {
    dispatch(setAssessmentFilters(ASSESSMENT_INIT_STATE));
};

/**
 * Filters the assessment list based on the current assessment filters in the state.
 *
 * @param {Array} list - The list of assessments to filter.
 * @returns {Array} - The filtered list of assessments.
 */
export const filterAssessmentList = (list) => (dispatch, getState) => {
    const {ageRestriction, specialities, clinics} = getState().patientContext.assessmentFilters;
    return _.filter(list, assessment => {
        const ageCheck = !ageRestriction?.length || _.some(assessment.ageRestriction, age => ageRestriction.some(ar => ar.__id === age));
        const specialtyCheck = !specialities?.length || _.some(assessment.specialties, spec => specialities.some(ar => ar.__id === spec));
        const clinicCheck = !clinics?.length || _.some(assessment.clinics, clinic => clinics.some(ar => ar.wardCode === clinic));
        return ageCheck && specialtyCheck && clinicCheck;
    });
};

export const patientContextClearStates = () => (dispatch) => {
    dispatch(setForeOpenNewClinicalNote(false));
    dispatch(setFormIsOpen(false));
    dispatch(setReferralModalClosed(false));
    dispatch(clearWorkflow());
    dispatch(setPatientType(null));
    dispatch(setPatientAgeType(null));
    dispatch(setPatientBannerType(PATIENT_BANNER_TYPE.UNSET));
    dispatch(setObservationType('obs'));
    dispatch(setObservationViewType('table'));
    dispatch(setObservationMethod(null));
    dispatch(setRemoveOption(null));
    dispatch(setDischargeTemplate(null));
    dispatch(setReferralFormData(null));
    dispatch(setBlockDischargeMandatoryAssessment(false));
    dispatch(setShowMandatoryAssessment(false));
    dispatch(setMandatoryAssessmentList([]));
    dispatch(setActionPanelDisableComponent([]));
    dispatch(closeAllActionPanel());
    dispatch(clearAssessmentFilter());
    dispatch(setCounterVisitsED(null));
    dispatch(setCounterVisitsIP(null));
    dispatch(setCtxCurrentPatientInWard(null));
};

export default patientContextSlice.reducer;
